import React, { useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { RootState, AppDispatch } from "../../../redux/store";
import {
  fetchBehaviorConfig,
  updateBehaviorConfig,
} from "../../../redux/actions/behaviorConfigActions";
import { format } from "date-fns";

const BehaviorConfigView: React.FC = () => {
  const { behaviorConfigId } = useParams<{ behaviorConfigId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();

  const { currentBehaviorConfig, loading, error } = useSelector(
    (state: RootState) => state.behaviorConfigs
  );

  const [isEditing, setIsEditing] = useState(false);
  const [editedBehaviorName, setEditedBehaviorName] = useState("");
  const [editedDemoMode, setEditedDemoMode] = useState("");

  useEffect(() => {
    if (behaviorConfigId) {
      dispatch(fetchBehaviorConfig(behaviorConfigId));
    }
  }, [dispatch, behaviorConfigId]);

  useEffect(() => {
    if (currentBehaviorConfig) {
      setEditedBehaviorName(currentBehaviorConfig.behavior_name);
      setEditedDemoMode(currentBehaviorConfig.demo_mode || "");
    }
  }, [currentBehaviorConfig]);

  useEffect(() => {
    // If a toast message was passed via navigation state, show it.
    if (location.state && (location.state as any).toastMessage) {
      toast.success((location.state as any).toastMessage);
      // Clear the state so the toast isn't shown again.
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    if (currentBehaviorConfig) {
      setEditedBehaviorName(currentBehaviorConfig.behavior_name);
      setEditedDemoMode(currentBehaviorConfig.demo_mode || "");
    }
  };

  const handleSave = () => {
    if (!behaviorConfigId || !editedBehaviorName) {
      toast.error("Behavior name is required");
      return;
    }

    const updateData = {
      id: behaviorConfigId,
      behavior_name: editedBehaviorName,
      demo_mode: editedDemoMode || null,
    };

    dispatch(updateBehaviorConfig(updateData))
      .unwrap()
      .then(() => {
        toast.success("Behavior Config updated successfully");
        setIsEditing(false);
      })
      .catch((error) => {
        toast.error(`Failed to update behavior config: ${error.message}`);
      });
  };

  if (loading && !currentBehaviorConfig) {
    return <CircularProgress />;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  if (!currentBehaviorConfig) {
    return <div>Behavior Config not found</div>;
  }

  return (
    <Box sx={{ p: 4 }}>
      <ToastContainer position="top-center" autoClose={3000} />
      <Typography variant="h5" gutterBottom>
        Behavior Config Details
      </Typography>

      <Paper elevation={3} sx={{ p: 3, mt: 2 }}>
        <TextField
          label="Config ID"
          value={currentBehaviorConfig.id}
          fullWidth
          margin="normal"
          InputProps={{
            readOnly: true,
            sx: {
              backgroundColor: "#f5f5f5",
              opacity: 0.7,
            },
          }}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          label="Behavior Name"
          value={isEditing ? editedBehaviorName : currentBehaviorConfig.behavior_name}
          onChange={(e) => setEditedBehaviorName(e.target.value)}
          fullWidth
          margin="normal"
          InputProps={{
            readOnly: !isEditing,
            sx: !isEditing
              ? {
                  backgroundColor: "#f5f5f5",
                  opacity: 0.7,
                }
              : {},
          }}
          InputLabelProps={{ shrink: true }}
        />

        <FormControl fullWidth margin="normal">
          <InputLabel>Demo Mode</InputLabel>
          <Select
            value={isEditing ? editedDemoMode : (currentBehaviorConfig.demo_mode || "")}
            onChange={(e) => setEditedDemoMode(e.target.value)}
            label="Demo Mode"
            disabled={!isEditing}
            sx={!isEditing
              ? {
                  backgroundColor: "#f5f5f5",
                  opacity: 0.7,
                }
              : {}}
          >
            <MenuItem value="">None</MenuItem>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="travel">Travel</MenuItem>
            <MenuItem value="sales">Sales</MenuItem>
            <MenuItem value="hr">HR</MenuItem>
            <MenuItem value="non-demo">Non-demo (default)</MenuItem>
          </Select>
        </FormControl>

        <TextField
          label="Created At"
          value={
            currentBehaviorConfig.created_at
              ? format(new Date(currentBehaviorConfig.created_at), "yyyy-MM-dd HH:mm:ss")
              : ""
          }
          fullWidth
          margin="normal"
          InputProps={{
            readOnly: true,
            sx: {
              backgroundColor: "#f5f5f5",
              opacity: 0.7,
            },
          }}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          label="Updated At"
          value={
            currentBehaviorConfig.updated_at
              ? format(new Date(currentBehaviorConfig.updated_at), "yyyy-MM-dd HH:mm:ss")
              : "Never"
          }
          fullWidth
          margin="normal"
          InputProps={{
            readOnly: true,
            sx: {
              backgroundColor: "#f5f5f5",
              opacity: 0.7,
            },
          }}
          InputLabelProps={{ shrink: true }}
        />

        <Box display="flex" alignItems="center" marginTop={3} gap={2}>
          {!isEditing ? (
            <>
              <Button variant="contained" color="primary" onClick={handleEdit}>
                Edit
              </Button>
              <Button
                variant="contained"
                color="warning"
                onClick={() => navigate("/dashboard/behavior-configs")}
              >
                Back
              </Button>
            </>
          ) : (
            <>
              <Button variant="contained" color="primary" onClick={handleSave}>
                Save
              </Button>
              <Button variant="outlined" color="secondary" onClick={handleCancel}>
                Cancel
              </Button>
            </>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default BehaviorConfigView;
